import React from "react";
import { useGetTicketByIdQuery } from "@/api/ticketApi/ticket";
import { MdCategory, MdSubdirectoryArrowRight } from "react-icons/md";
import { Badge } from "flowbite-react";

export default function TicketDetailsInfo({ ticketId }: { ticketId: string }) {
  const {
    data: ticketDetails,
    isLoading,
    error,
  } = useGetTicketByIdQuery(ticketId);

  if (isLoading) {
    return (
      <p className="text-gray-600 dark:text-gray-400">
        Loading ticket details...
      </p>
    );
  }

  if (error || !ticketDetails) {
    return <p className="text-red-600">Failed to load ticket details.</p>;
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="flex flex-col gap-28 lg:flex-row lg:items-start lg:justify-between">
        {/* Left Section */}
        <div className="w-full">
          <div className="max-w-full flex-1 space-y-2">
            <h2 className="text-sm text-gray-500">Ticket Ref Id: {ticketId}</h2>
            <h1 className="text-2xl font-medium">
              {ticketDetails.ticket_title || "N/A"}
            </h1>
            <div className="mt-6">
              <p className="text-sm leading-6">
                {ticketDetails.ticket_description ||
                  "No description available."}
              </p>
            </div>
          </div>
          <div className="mt-4 flex w-full justify-end text-sm">
            <div className="rounded-md bg-pink-50 p-5">
              <p>
                Created on{" "}
                <span className="font-medium">
                  {new Date(ticketDetails.createdAt).toLocaleDateString()}
                </span>
              </p>
              <p>
                By{" "}
                <span className="font-medium">
                  {ticketDetails.created_by_agent?.agent_username || "Unknown"}
                </span>
              </p>
              <p>
                For{" "}
                <span className="font-medium">
                  {ticketDetails.client?.client_username || "Unknown"}
                </span>
              </p>
              <p>
                Assigned Agent:{" "}
                <span className="font-medium">
                  {ticketDetails.assigned_agent?.agent_username || "Unassigned"}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="mr-20 mt-0 space-y-4 lg:mt-10">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm">
              <MdCategory className="text-lg text-gray-500" />
              <span className="font-medium">
                {ticketDetails.category?.category_name || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MdSubdirectoryArrowRight className="text-lg text-gray-500" />
              <span className="font-medium">
                {ticketDetails.subcategory?.subcategory_name || "N/A"}
              </span>
            </div>
            <div className="ml-5 flex items-center gap-2 text-sm">
              <Badge
                color={
                  ticketDetails.status?.status_name?.toLowerCase() ===
                  "completed"
                    ? "success"
                    : ticketDetails.status?.status_name?.toLowerCase() ===
                        "resolved"
                      ? "blue"
                      : ticketDetails.status?.status_name?.toLowerCase() ===
                          "open"
                        ? "warning"
                        : ticketDetails.status?.status_name?.toLowerCase() ===
                            "closed"
                          ? "dark"
                          : "failure"
                }
                className="flex max-w-[100px] items-center justify-center"
              >
                {ticketDetails.status?.status_name || "Unknown"}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
